import { validateBetaInviteCodePublic } from "@/api/publicBetaInvite";
import { Button } from "@/components/ui/button";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { PENDING_BETA_INVITE_CODE_KEY } from "@/constants/betaInvite";
import { ROUTES } from "@/constants/routes";
import LogoIcon from "@/svg/brand/cohesive-logo-icon.svg?react";
import { motion } from "framer-motion";
import { REGEXP_ONLY_DIGITS_AND_CHARS } from "input-otp";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { CenteredLayout } from "@/layouts/CenteredLayout";

const CODE_LEN = 6;

const BETA_INVITE_CODE_ERROR_ID = "beta-invite-code-error";

function normalizeFromQuery(raw: string | null): string {
  if (!raw) {
    return "";
  }
  return raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, CODE_LEN);
}

function betaInviteApiErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    return err.message;
  }
  return "Something went wrong. Please try again.";
}

export function BetaInvitePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initial = useMemo(
    () => normalizeFromQuery(searchParams.get("code")),
    [searchParams],
  );
  const [value, setValue] = useState(initial);
  const [isValidating, setIsValidating] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const codeInvalid = validationError != null;

  useEffect(() => {
    setValue((v) => {
      const next = normalizeFromQuery(searchParams.get("code"));
      if (next && (!v || v.length < next.length)) {
        return next;
      }
      return v;
    });
  }, [searchParams]);

  const handleContinue = async () => {
    const code = value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, CODE_LEN);
    if (code.length !== CODE_LEN || isValidating) {
      return;
    }
    setIsValidating(true);
    setValidationError(null);
    try {
      await validateBetaInviteCodePublic(code);
      sessionStorage.setItem(PENDING_BETA_INVITE_CODE_KEY, code);
      navigate(ROUTES.INVITE_CONTINUE, { replace: true });
    } catch (err) {
      setValidationError(betaInviteApiErrorMessage(err));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <>
      <title>Beta invite</title>
      <CenteredLayout maxWidth="md">
        <div className="space-y-8 text-center">
          <div className="space-y-6">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ type: "spring", stiffness: 100, damping: 15 }}
              className="flex justify-center"
            >
              <LogoIcon width="64" height="64" />
            </motion.div>

            <motion.h1
              className="text-3xl sm:text-5xl font-bold text-foreground"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15, duration: 0.4, ease: "easeOut" }}
            >
              Welcome to Cohesive
            </motion.h1>

            <motion.p
              className="text-base sm:text-xl text-muted-foreground max-w-2xl mx-auto font-medium tracking-tight px-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.4, ease: "easeOut" }}
            >
              Enter your beta code to gain access
            </motion.p>
          </div>

          <div className="flex flex-col items-center gap-6">
            <motion.div
              className="flex flex-col items-center gap-2"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.4, ease: "easeOut" }}
            >
              <InputOTP
                maxLength={CODE_LEN}
                pattern={REGEXP_ONLY_DIGITS_AND_CHARS}
                value={value}
                onChange={(v) => {
                  setValue(v.toUpperCase());
                  setValidationError(null);
                }}
                containerClassName="gap-2"
                aria-invalid={codeInvalid}
                aria-describedby={
                  codeInvalid ? BETA_INVITE_CODE_ERROR_ID : undefined
                }
              >
                <InputOTPGroup className="has-aria-invalid:border-destructive has-aria-invalid:ring-3 has-aria-invalid:ring-destructive/20 dark:has-aria-invalid:ring-destructive/40 flex items-center rounded-lg *:data-[slot=input-otp-slot]:h-12 *:data-[slot=input-otp-slot]:w-11 *:data-[slot=input-otp-slot]:text-xl">
                  {Array.from({ length: CODE_LEN }, (_, index) => (
                    <InputOTPSlot
                      data-slot="input-otp-slot"
                      className="relative flex size-8 items-center justify-center border-y border-r border-input text-sm transition-all outline-none first:rounded-l-lg first:border-l last:rounded-r-lg aria-invalid:border-destructive data-[active=true]:z-10 data-[active=true]:border-ring data-[active=true]:ring-3 data-[active=true]:ring-ring/50 data-[active=true]:aria-invalid:border-destructive data-[active=true]:aria-invalid:ring-destructive/20 dark:bg-input/30 dark:data-[active=true]:aria-invalid:ring-destructive/40"
                      key={index}
                      index={index}
                      aria-invalid={codeInvalid}
                      aria-describedby={
                        codeInvalid ? BETA_INVITE_CODE_ERROR_ID : undefined
                      }
                    />
                  ))}
                </InputOTPGroup>
              </InputOTP>
              {validationError ? (
                <p
                  id={BETA_INVITE_CODE_ERROR_ID}
                  role="alert"
                  className="text-sm text-destructive text-center max-w-md px-2"
                >
                  {validationError}
                </p>
              ) : null}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.65, duration: 0.35, ease: "easeOut" }}
            >
              <Button
                type="button"
                size="lg"
                disabled={
                  isValidating ||
                  value.replace(/[^A-Z0-9]/gi, "").length !== CODE_LEN
                }
                onClick={() => void handleContinue()}
                isLoading={isValidating}
              >
                Continue
              </Button>
            </motion.div>
          </div>
        </div>
      </CenteredLayout>
    </>
  );
}
